
import React, { useEffect, useState } from 'react';
import { fetchFearAndGreedIndex } from '../services/marketService';
import { FearGreedData } from '../types';

interface FearGreedWidgetProps {
  theme: 'dark' | 'light';
}

// Helper to convert polar coordinates to cartesian
const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = (angleInDegrees) * Math.PI / 180.0;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY - (radius * Math.sin(angleInRadians))
  };
};

const FearGreedWidget: React.FC<FearGreedWidgetProps> = ({ theme }) => {
  const [data, setData] = useState<FearGreedData | null>(null);

  useEffect(() => {
    fetchFearAndGreedIndex().then(setData);
  }, []);

  if (!data) return null;

  const value = parseInt(data.value);
  
  // Colors matching standard Fear & Greed representation
  const colors = [
    '#ef4444', // Red (Extreme Fear)
    '#f97316', // Orange (Fear)
    '#eab308', // Yellow (Neutral)
    '#84cc16', // Lime (Greed)
    '#22c55e'  // Green (Extreme Greed)
  ];

  let currentColor = colors[2];
  if (value < 25) currentColor = colors[0];
  else if (value < 45) currentColor = colors[1];
  else if (value < 55) currentColor = colors[2];
  else if (value < 75) currentColor = colors[3];
  else currentColor = colors[4];

  // Rotation logic: 0 to 180 degrees mapping to -90 to 90 css rotation
  const rotation = (value / 100) * 180 - 90;

  // Calculate time until update
  const timeUntil = parseInt(data.time_until_update || '0');
  const hoursLeft = Math.floor(timeUntil / 3600);
  const minutesLeft = Math.floor((timeUntil % 3600) / 60);
  const updateTooltip = `Próxima actualización en: ${hoursLeft}h ${minutesLeft}m`;

  // Translate API labels to Spanish
  const getSpanishLabel = (label: string) => {
    switch (label) {
        case 'Extreme Fear': return 'Miedo Extremo';
        case 'Fear': return 'Miedo';
        case 'Neutral': return 'Neutral';
        case 'Greed': return 'Codicia';
        case 'Extreme Greed': return 'Codicia Extrema';
        default: return label;
    }
  };

  // SVG Geometry configuration
  const radius = 40; 
  const stroke = 20; 
  const center = { x: 50, y: 50 };
  const segments = [
      { color: colors[0], start: 180, end: 144 },
      { color: colors[1], start: 144, end: 108 },
      { color: colors[2], start: 108, end: 72 },
      { color: colors[3], start: 72, end: 36 },
      { color: colors[4], start: 36, end: 0 },
  ];

  const getSegmentPath = (start: number, end: number) => {
      const startP = polarToCartesian(center.x, center.y, radius, start);
      const endP = polarToCartesian(center.x, center.y, radius, end);
      return `M ${startP.x} ${startP.y} A ${radius} ${radius} 0 0 1 ${endP.x} ${endP.y}`;
  };

  return (
    <div 
        title={updateTooltip}
        className="flex items-center space-x-3 select-none cursor-help"
    >
        <div className="relative w-20 h-10 overflow-hidden flex items-end justify-center">
            {/* Layer 1: Gauge Arcs (SVG) */}
            <svg viewBox="0 0 100 50" className="w-full h-full absolute top-0 left-0 z-0">
                {segments.map((seg, i) => (
                    <path 
                        key={i} 
                        d={getSegmentPath(seg.start, seg.end)} 
                        fill="none" 
                        stroke={seg.color} 
                        strokeWidth={stroke} 
                    />
                ))}
            </svg>
            
            {/* Layer 2: Needle */}
            <div 
                className="absolute bottom-0 left-1/2 w-0 h-0 z-10"
                style={{ transform: `rotate(${rotation}deg)`, transformOrigin: 'bottom center' }}
            >
                 <div className={`w-[2px] h-11 absolute bottom-0 left-[-1px] rounded-t-full origin-bottom ${theme === 'dark' ? 'bg-white' : 'bg-slate-800'}`}></div>
            </div>

            {/* Layer 3: Inner Hub (Dark Semicircle) */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-[60%] bg-[#1e293b] rounded-t-full z-20 flex items-end justify-center pb-0.5 shadow-[0_-2px_8px_rgba(0,0,0,0.3)]">
                 <span className="text-white text-lg font-black tracking-tighter leading-none mb-0.5">{value}</span>
            </div>
        </div>
        
        <div className="flex flex-col justify-center leading-tight">
            <span className="text-[8px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Sentimiento</span>
            <span className="text-[10px] font-black uppercase tracking-tight whitespace-nowrap" style={{ color: currentColor }}>
                {getSpanishLabel(data.value_classification)}
            </span>
        </div>
    </div>
  );
};

export default FearGreedWidget;
